/**
 * API Key Service (Frontend)
 * Handles API calls for API key management
 */

import { supabase } from '../utils/supabaseClient';
import {
  ApiKey,
  CreateApiKeyRequest,
  ApiKeyResponse,
  ApiKeyWithRole,
} from '../types/apiKey';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/**
 * Get authorization header with Supabase JWT
 */
const getAuthHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session?.access_token) {
    throw new Error('No active session');
  }

  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session.access_token}`,
  };
};

/**
 * Create a new API key
 */
export const createApiKey = async (
  data: CreateApiKeyRequest
): Promise<ApiKeyResponse> => {
  try {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}/api/developers/api-keys`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create API key');
    }

    const result = await response.json();
    
    // Convert date strings to Date objects
    return {
      ...result,
      expiresAt: new Date(result.expiresAt),
      createdAt: new Date(result.createdAt),
    };
  } catch (error) {
    console.error('[ApiKeyService] Error creating API key:', error);
    throw error;
  }
};

/**
 * Get all API keys for the current tenant
 */
export const getApiKeys = async (): Promise<ApiKey[]> => {
  try {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}/api/developers/api-keys`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch API keys');
    }

    const data = await response.json();
    
    // Convert date strings to Date objects
    return data.map((key: any) => ({
      ...key,
      expiresAt: new Date(key.expiresAt),
      revokedAt: key.revokedAt ? new Date(key.revokedAt) : null,
      createdAt: new Date(key.createdAt),
    }));
  } catch (error) {
    console.error('[ApiKeyService] Error fetching API keys:', error);
    throw error;
  }
};

/**
 * Revoke an API key
 */
export const revokeApiKey = async (id: string): Promise<void> => {
  try {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}/api/developers/api-keys/${id}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to revoke API key');
    }
  } catch (error) {
    console.error('[ApiKeyService] Error revoking API key:', error);
    throw error;
  }
};

/**
 * Assign a role to an API key
 */
export const assignRoleToApiKey = async (
  keyId: string,
  roleId: string
): Promise<void> => {
  try {
    const headers = await getAuthHeaders();

    const response = await fetch(
      `${API_BASE_URL}/api/developers/api-keys/${keyId}/role`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ roleId }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to assign role');
    }
  } catch (error) {
    console.error('[ApiKeyService] Error assigning role:', error);
    throw error;
  }
};

/**
 * Get roles assigned to an API key
 */
export const getApiKeyRoles = async (keyId: string): Promise<any[]> => {
  try {
    const headers = await getAuthHeaders();

    const response = await fetch(
      `${API_BASE_URL}/api/developers/api-keys/${keyId}/roles`,
      {
        method: 'GET',
        headers,
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch API key roles');
    }

    return await response.json();
  } catch (error) {
    console.error('[ApiKeyService] Error fetching API key roles:', error);
    throw error;
  }
};
